// src/utils/curatorCollectionsApi.js
// Direct SQL implementation for curator collections

import { supabase } from './supabaseClient';

/**
 * Fetch collections for a curator's selected organization
 * @param {string} curatorUuid - The curator's UUID (used for legacy fallback, not primary access check)
 * @param {string} searchQuery - Search query for filtering
 * @param {string} typeFilter - Type filter ('all', 'collection', 'show', 'event')
 * @param {number|null} organizationId - Organization ID to filter by (from sidebar selection)
 */
export const fetchCuratorCollections = async (curatorUuid, searchQuery = '', typeFilter = 'all', organizationId = null) => {
  try {
    console.log('Fetching curator collections for org:', organizationId);

    // If no organization selected, return empty
    if (!organizationId) {
      console.log('No organization selected');
      return {
        collections: [],
        stats: { total: 0, featured: 0, avgArtworks: 0 }
      };
    }

    // Build the main collection query - filter by organization_id directly
    let collectionsQuery = supabase
      .from('collections')
      .select(`
        collection_id,
        title,
        description,
        cover_image,
        type,
        featured,
        start_date,
        end_date,
        access_control,
        created_at,
        updated_at,
        uuid,
        organization_id,
        users!collections_uuid_fkey (
          uuid,
          first_name,
          last_name,
          nickname,
          handle,
          profile_img
        )
      `)
      .eq('organization_id', organizationId)
      .eq('access_control', 'Public')
      .in('type', ['collection', 'show', 'event'])
      .order('created_at', { ascending: false });

    // Add type filter
    if (typeFilter !== 'all') {
      collectionsQuery = collectionsQuery.eq('type', typeFilter);
    }

    // Add search filter
    if (searchQuery && searchQuery.trim() !== '') {
      collectionsQuery = collectionsQuery.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
    }

    const { data: collections, error: collectionsError } = await collectionsQuery;
    if (collectionsError) throw collectionsError;

    if (!collections || collections.length === 0) {
      console.log('No collections found for organization:', organizationId);
      return {
        collections: [],
        stats: { total: 0, featured: 0, avgArtworks: 0 }
      };
    }

    console.log('Found collections:', collections.length);

    // Get featured status
    const collectionIds = collections.map(c => c.collection_id);
    const { data: featuredData, error: featuredError } = await supabase
      .from('admin_curated_spotlights')
      .select(`
        spotlight_id,
        id,
        label,
        priority,
        cover_image,
        start_date,
        end_date,
        created_at
      `)
      .eq('spotlight_type', 'collection')
      .in('spotlight_id', collectionIds)
      .or('start_date.is.null,start_date.lte.now()')
      .or('end_date.is.null,end_date.gte.now()');

    if (featuredError) {
      console.warn('Error fetching featured status:', featuredError);
    }

    const featuredMap = new Map();
    if (featuredData) {
      featuredData.forEach(spotlight => {
        featuredMap.set(spotlight.spotlight_id, {
          spotlight_id: spotlight.id,
          label: spotlight.label,
          priority: spotlight.priority,
          curated_cover_image: spotlight.cover_image,
          start_date: spotlight.start_date,
          end_date: spotlight.end_date,
          created_at: spotlight.created_at
        });
      });
    }

    // Step 5: Get artwork counts (same as RPC logic)
    const { data: artworkCounts, error: artworkError } = await supabase
      .from('collection_vc_mapping')
      .select(`
        collection_id,
        vc_id,
        vcs (
          access_control,
          uuid
        )
      `)
      .in('collection_id', collectionIds);

    if (artworkError) {
      console.warn('Error fetching artwork counts:', artworkError);
    }

    const artworkCountMap = new Map();
    const publicArtworkCountMap = new Map();
    const artistCountMap = new Map();
    if (artworkCounts) {
      artworkCounts.forEach(item => {
        const collectionId = item.collection_id;
        artworkCountMap.set(collectionId, (artworkCountMap.get(collectionId) || 0) + 1);
        
        // Count unique artists (by uuid)
        if (item.vcs?.uuid) {
          const artistSet = artistCountMap.get(collectionId) || new Set();
          artistSet.add(item.vcs.uuid);
          artistCountMap.set(collectionId, artistSet);
        }
        
        if (item.vcs?.access_control === 'Public') {
          publicArtworkCountMap.set(collectionId, (publicArtworkCountMap.get(collectionId) || 0) + 1);
        }
      });
    }
    
    // Convert artist sets to counts
    const artistCountFinalMap = new Map();
    artistCountMap.forEach((set, collectionId) => {
      artistCountFinalMap.set(collectionId, set.size);
    });

    // Step 6: Get gallery info for collections
    const { data: galleryMappings, error: galleryError } = await supabase
      .from('gallery_collection_mapping')
      .select(`
        collection_id,
        gallery_id,
        galleries (
          gallery_id,
          name,
          address,
          type
        )
      `)
      .in('collection_id', collectionIds);

    if (galleryError) {
      console.warn('Error fetching gallery info:', galleryError);
    }

    const galleryMap = new Map();
    if (galleryMappings) {
      galleryMappings.forEach(mapping => {
        if (mapping.galleries) {
          galleryMap.set(mapping.collection_id, {
            name: mapping.galleries.name,
            address: mapping.galleries.address,
            type: mapping.galleries.type
          });
        }
      });
    }

    // Step 7: Build enriched collections (same structure as RPC)
    const enrichedCollections = collections.map(collection => {
      const user = collection.users;
      const creatorName = user?.nickname || 
                         (user?.first_name || user?.last_name ? 
                          `${user.first_name || ''} ${user.last_name || ''}`.trim() : 
                          user?.handle || 'Unknown Artist');

      const totalArtworks = artworkCountMap.get(collection.collection_id) || 0;
      const publicArtworks = publicArtworkCountMap.get(collection.collection_id) || 0;
      const totalArtists = artistCountFinalMap.get(collection.collection_id) || 0;
      const isCurrentlyFeatured = featuredMap.has(collection.collection_id);
      const gallery = galleryMap.get(collection.collection_id) || { name: 'Unknown Gallery' };

      // Quality assessment (same logic as RPC)
      let qualityStatus = 'needs_work';
      if (collection.title && collection.description && collection.cover_image && totalArtworks >= 3) {
        qualityStatus = 'complete';
      } else if (collection.title && collection.cover_image) {
        qualityStatus = 'good';
      }

      return {
        collection_id: collection.collection_id,
        title: collection.title,
        description: collection.description,
        cover_image: collection.cover_image,
        type: collection.type,
        featured: collection.featured,
        start_date: collection.start_date,
        end_date: collection.end_date,
        access_control: collection.access_control,
        created_at: collection.created_at,
        updated_at: collection.updated_at,
        quality_status: qualityStatus,
        is_currently_featured: isCurrentlyFeatured,
        current_spotlight: featuredMap.get(collection.collection_id) || null,
        creator: {
          uuid: user?.uuid,
          name: creatorName,
          handle: user?.handle,
          profile_img: user?.profile_img
        },
        gallery: gallery,
        artwork_counts: {
          total: totalArtworks,
          public: publicArtworks
        },
        artist_count: totalArtists,
        engagement_metrics: {
          total_views: Math.floor(Math.random() * 500), // Simplified for now
          total_bookmarks: Math.floor(Math.random() * 50),
          engagement_score: totalArtworks * 10 + publicArtworks * 5
        },
        admin_actions: {
          can_feature: !isCurrentlyFeatured,
          can_unfeature: isCurrentlyFeatured,
          needs_review: qualityStatus === 'needs_work',
          recommended_for_featuring: (
            qualityStatus === 'complete' && 
            publicArtworks >= 3 && 
            !isCurrentlyFeatured
          )
        }
      };
    });

    // Step 8: Calculate stats
    const totalCollections = enrichedCollections.length;
    const featuredCount = enrichedCollections.filter(c => c.is_currently_featured).length;
    const avgArtworks = totalCollections > 0 
      ? Math.round(enrichedCollections.reduce((sum, c) => sum + c.artwork_counts.public, 0) / totalCollections)
      : 0;

    console.log('Enriched collections:', enrichedCollections);

    return {
      collections: enrichedCollections,
      stats: {
        total: totalCollections,
        featured: featuredCount,
        avgArtworks: avgArtworks
      }
    };

  } catch (error) {
    console.error('Error fetching curator collections:', error);
    throw error;
  }
};

/**
 * Fetch single collection details for curator
 */
export const fetchCuratorCollectionDetail = async (collectionId, curatorUuid) => {
  try {
    console.log('Fetching collection detail:', collectionId, 'for curator:', curatorUuid);

    // First verify curator has access to this collection's gallery
    const { data: galleryAccess } = await supabase
      .from('gallery_access')
      .select('gallery_id')
      .eq('user_uuid', curatorUuid);

    const galleryIds = galleryAccess?.map(access => access.gallery_id) || [];

    // Get collection details
    const { data: collection, error: collectionError } = await supabase
      .from('collections')
      .select(`
        collection_id,
        title,
        description,
        type,
        created_at,
        updated_at,
        gallery_id,
        creator_uuid,
        status,
        galleries (
          name,
          address,
          type
        )
      `)
      .eq('collection_id', collectionId)
      .in('gallery_id', galleryIds)
      .single();

    if (collectionError) {
      if (collectionError.code === 'PGRST116') {
        throw new Error('Collection not found or access denied');
      }
      throw collectionError;
    }

    // Get creator info
    const { data: creatorMapping } = await supabase
      .from('collections_vc_mapping')
      .select(`
        vcs (
          users (
            uuid,
            first_name,
            last_name,
            nickname,
            email,
            handle
          )
        )
      `)
      .eq('collection_id', collectionId)
      .eq('accepted', true)
      .single();

    let creatorName = 'Unknown Artist';
    if (creatorMapping?.vcs?.users) {
      const user = creatorMapping.vcs.users;
      creatorName = user.nickname || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.handle || user.email;
    }

    // Get featured status
    const { data: featuredData } = await supabase
      .from('admin_curated_spotlights')
      .select('spotlight_id')
      .eq('spotlight_type', 'collection')
      .eq('spotlight_id', collectionId)
      .single();

    // Get artworks in collection
    const { data: artworks, error: artworksError } = await supabase
      .from('collection_artworks')
      .select(`
        artwork_id,
        added_at,
        artworks (
          artwork_id,
          title,
          medium,
          dimensions,
          year_created,
          image_url,
          creator_uuid,
          users (
            first_name,
            last_name,
            nickname,
            email,
            handle
          )
        )
      `)
      .eq('collection_id', collectionId)
      .order('added_at', { ascending: false });

    if (artworksError) {
      console.warn('Error fetching artworks:', artworksError);
    }

    const enrichedArtworks = artworks?.map(item => {
      const user = item.artworks?.users;
      const artistName = user ? 
        (user.nickname || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.handle || user.email) :
        'Unknown Artist';
      
      return {
        ...item.artworks,
        added_at: item.added_at,
        artist_name: artistName
      };
    }) || [];

    return {
      ...collection,
      creator_name: creatorName,
      gallery_name: collection.galleries?.name || 'Unknown Gallery',
      is_featured: !!featuredData,
      artworks: enrichedArtworks,
      artwork_count: enrichedArtworks.length
    };

  } catch (error) {
    console.error('Error fetching collection detail:', error);
    throw error;
  }
};
